/* eslint-disable no-console */
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  runTransaction
} from "firebase/firestore";
import _ from "lodash";

const state = {
  // Translation data
  translations: [],
  translationData: [],
  currentTranslation: null,

  // Loading states
  loadingTranslations: false,
  loadingTranslationData: false,
  loadingTranslation: false,
  savingTranslation: false,
  deletingTranslation: false,
  processingTranslation: false,

  // Translation state management
  translationChanged: false,
  translationUpdates: {},

  // Error state
  error: null
};

const actions = {
  /**
   * Load all translations for the program
   */
  async loadTranslations({ commit, getters }) {
    commit("setLoadingTranslations", true);
    commit("setTranslationError", null);

    try {
      const programId = getters.programId;
      if (!programId) {
        console.log("No program ID available for loading translations");
        commit("setTranslations", []);
        return [];
      }

      const programRef = doc(db(), "programs", programId);
      const translationsRef = collection(programRef, "translations");
      const translationsQuery = query(
        translationsRef,
        orderBy("titleUppercase")
      );
      const querySnapshot = await getDocs(translationsQuery);

      const translations = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        translations.push({
          id: docSnapshot.id,
          title: data.title || "",
          titleUppercase: data.titleUppercase || "",
          locale: data.locale || "",
          directory: data.directory || {},
          status: data.status || "Draft",
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date(),
          createdBy: data.createdBy || "",
          updatedBy: data.updatedBy || ""
        });
      });

      commit("setTranslations", translations);
      return translations;
    } catch (error) {
      console.error("Error loading translations:", error);
      commit("setTranslationError", error.message);
      commit("setTranslations", []);
      throw error;
    } finally {
      commit("setLoadingTranslations", false);
    }
  },

  /**
   * Load translation data for i18n processing
   */
  async loadTranslationData({ commit, getters, dispatch }) {
    commit("setLoadingTranslationData", true);
    commit("setTranslationError", null);

    try {
      const programId = getters.programId;
      if (!programId) {
        console.log("No program ID available for loading translation data");
        commit("setTranslationData", {});
        return {};
      }

      const programRef = doc(db(), "programs", programId);
      const translationsRef = collection(programRef, "translations");
      const querySnapshot = await getDocs(translationsRef);

      const translationData = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        translationData.push({
          locale: data.locale || "",
          directory: { ...data.directory } || {}
        });
      });

      const result = Object.keys(translationData).reduce(
        (translationObj, key) => {
          const { directory, locale } = translationData[key];
          const directories = Object.keys(directory).reduce(
            (directoryObj, directoryKey) => {
              const directoryData = directory[directoryKey];
              if (directoryData && typeof directoryData === "object") {
                const { english, value } = directoryData;
                return { ...directoryObj, [english]: value };
              }
              return directoryObj;
            },
            {}
          );
          return { ...translationObj, [locale]: directories };
        },
        {}
      );

      commit("setTranslationData", result);

      // Update i18n with the loaded translations
      dispatch("updateI18nTranslations", result);

      return result;
    } catch (error) {
      console.error("Error loading translation data:", error);
      commit("setTranslationError", error.message);
      commit("setTranslationData", {});
      throw error;
    } finally {
      commit("setLoadingTranslationData", false);
    }
  },

  /**
   * Update i18n with translation data
   */
  updateI18nTranslations({ getters }, translationData = null) {
    try {
      // Use provided data or get from store
      const data = translationData || getters.translationData;

      if (!data || Object.keys(data).length === 0) {
        console.log("No translation data available for i18n update");
        return;
      }

      // Import i18n instance
      const i18n = require("@/plugins/i18n").default;

      // Update each locale
      Object.keys(data).forEach(locale => {
        if (data[locale] && Object.keys(data[locale]).length > 0) {
          console.log(`🌐 Updating i18n for locale: ${locale}`);
          i18n.updateTranslations(data[locale], locale);
        }
      });

      // Set the initial locale from program settings
      const initialLocale = getters.initialLocale || "en";
      i18n.setLocaleWithFallback(initialLocale);

      console.log("✅ i18n translations updated successfully");
    } catch (error) {
      console.error("Error updating i18n translations:", error);
    }
  },

  /**
   * Load a specific translation by ID
   */
  async loadTranslation({ commit, getters }, translationId) {
    if (!translationId) {
      throw new Error("Translation ID is required");
    }

    commit("setLoadingTranslation", true);
    commit("setTranslationError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const translationRef = doc(
        collection(programRef, "translations"),
        translationId
      );
      const translationDoc = await getDoc(translationRef);

      if (!translationDoc.exists()) {
        throw new Error("Translation not found");
      }

      const data = translationDoc.data();
      const translation = {
        id: translationDoc.id,
        title: data.title || "",
        titleUppercase: data.titleUppercase || "",
        locale: data.locale || "",
        directory: data.directory || {},
        status: data.status || "Draft",
        created: data.created?.toDate() || new Date(),
        updated: data.updated?.toDate() || new Date(),
        createdBy: data.createdBy || "",
        updatedBy: data.updatedBy || ""
      };

      commit("setCurrentTranslation", translation);
      return translation;
    } catch (error) {
      console.error("Error loading translation:", error);
      commit("setTranslationError", error.message);
      throw error;
    } finally {
      commit("setLoadingTranslation", false);
    }
  },

  /**
   * Create a new translation
   */
  async createTranslation({ commit, getters, dispatch }, translationData) {
    if (!translationData || !translationData.title || !translationData.locale) {
      throw new Error("Translation title and locale are required");
    }

    commit("setSavingTranslation", true);
    commit("setTranslationError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const translationsRef = collection(programRef, "translations");

      // Check for duplicate titles within the same locale
      const titleUppercase = translationData.title.toUpperCase();
      const duplicateQuery = query(
        translationsRef,
        where("titleUppercase", "==", titleUppercase),
        where("locale", "==", translationData.locale)
      );
      const duplicateSnapshot = await getDocs(duplicateQuery);

      if (!duplicateSnapshot.empty) {
        throw new Error(
          "A translation with this title already exists for this locale"
        );
      }

      const translation = {
        title: translationData.title,
        titleUppercase,
        locale: translationData.locale,
        directory: translationData.directory || {},
        status: translationData.status || "Draft",
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: getters.user?.id || "system",
        updatedBy: getters.user?.id || "system"
      };

      const newTranslationRef = await addDoc(translationsRef, translation);

      const tempTranslation = {
        ...translation,
        id: newTranslationRef.id,
        created: new Date(),
        updated: new Date()
      };

      commit("createTranslation", tempTranslation);
      dispatch("setSnackbar", "Translation created successfully");

      return {
        success: true,
        translationId: newTranslationRef.id,
        translation: tempTranslation
      };
    } catch (error) {
      console.error("Error creating translation:", error);
      commit("setTranslationError", error.message);
      throw error;
    } finally {
      commit("setSavingTranslation", false);
    }
  },

  /**
   * Update an existing translation
   */
  async updateTranslation({ commit, getters, dispatch }, payload) {
    if (!payload || !getters.currentTranslation?.id) {
      throw new Error("Translation data and current translation are required");
    }

    commit("setSavingTranslation", true);
    commit("setTranslationError", null);

    try {
      const id = getters.currentTranslation.id;
      const programRef = doc(db(), "programs", getters.programId);
      const translationsRef = collection(programRef, "translations");
      const translationRef = doc(translationsRef, id);

      // Check for duplicate titles within the same locale (excluding current translation)
      if (payload.title && payload.locale) {
        const titleUppercase = payload.title.toUpperCase();
        const duplicateQuery = query(
          translationsRef,
          where("titleUppercase", "==", titleUppercase),
          where("locale", "==", payload.locale)
        );
        const duplicateSnapshot = await getDocs(duplicateQuery);

        let duplicated = false;
        duplicateSnapshot.forEach(docSnapshot => {
          if (docSnapshot.id !== id) {
            duplicated = true;
          }
        });

        if (duplicated) {
          throw new Error(
            "A translation with this title already exists for this locale"
          );
        }
      }

      const updateData = {
        ...payload,
        updated: serverTimestamp(),
        updatedBy: getters.user?.id || "system"
      };

      // Add titleUppercase if title is being updated
      if (payload.title) {
        updateData.titleUppercase = payload.title.toUpperCase();
      }

      await updateDoc(translationRef, updateData);

      const tempTranslation = {
        ...getters.currentTranslation,
        ...payload,
        updated: new Date(),
        updatedBy: getters.user?.id || "system"
      };

      if (payload.title) {
        tempTranslation.titleUppercase = payload.title.toUpperCase();
      }

      commit("updateTranslation", tempTranslation);
      dispatch("setSnackbar", "Translation updated successfully");
      dispatch("resetTranslationUpdates");

      return tempTranslation;
    } catch (error) {
      console.error("Error updating translation:", error);
      commit("setTranslationError", error.message);
      if (error.message.includes("already exists")) {
        throw error;
      }
      throw new Error("Error when updating translation");
    } finally {
      commit("setSavingTranslation", false);
    }
  },

  /**
   * Delete a translation
   */
  async deleteTranslation({ commit, getters, dispatch }, translationId) {
    if (!translationId) {
      throw new Error("Translation ID is required");
    }

    commit("setDeletingTranslation", true);
    commit("setTranslationError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const translationRef = doc(
        collection(programRef, "translations"),
        translationId
      );

      await deleteDoc(translationRef);

      commit("deleteTranslation", translationId);
      dispatch("setSnackbar", "Translation deleted successfully");

      return true;
    } catch (error) {
      console.error("Error deleting translation:", error);
      commit("setTranslationError", error.message);
      throw new Error("Error when deleting the translation");
    } finally {
      commit("setDeletingTranslation", false);
    }
  },

  /**
   * Get translation by ID
   */
  async getTranslationById({ getters }, translationId) {
    if (!translationId) {
      console.error("Translation ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const translationRef = doc(
        collection(programRef, "translations"),
        translationId
      );
      const translationDoc = await getDoc(translationRef);

      if (translationDoc.exists()) {
        const data = translationDoc.data();
        return {
          id: translationDoc.id,
          title: data.title || "",
          titleUppercase: data.titleUppercase || "",
          locale: data.locale || "",
          directory: data.directory || {},
          status: data.status || "Draft",
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date(),
          createdBy: data.createdBy || "",
          updatedBy: data.updatedBy || ""
        };
      } else {
        console.warn(`Translation with ID ${translationId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting translation by ID:", error);
      return null;
    }
  },

  /**
   * Duplicate a translation
   */
  async duplicateTranslation(
    { dispatch },
    { translationId, newTitle, newLocale }
  ) {
    if (!translationId || !newTitle || !newLocale) {
      throw new Error("Translation ID, new title, and new locale are required");
    }

    try {
      const originalTranslation = await dispatch(
        "getTranslationById",
        translationId
      );
      if (!originalTranslation) {
        throw new Error("Original translation not found");
      }

      const duplicatedTranslationData = {
        title: newTitle,
        locale: newLocale,
        directory: _.cloneDeep(originalTranslation.directory),
        status: "Draft"
      };

      const result = await dispatch(
        "createTranslation",
        duplicatedTranslationData
      );
      return result;
    } catch (error) {
      console.error("Error duplicating translation:", error);
      throw error;
    }
  },

  /**
   * Update translation directory (key-value pairs)
   */
  async updateTranslationDirectory(
    { commit, getters, dispatch },
    { translationId, directory }
  ) {
    if (!translationId || !directory) {
      throw new Error("Translation ID and directory data are required");
    }

    commit("setProcessingTranslation", true);
    commit("setTranslationError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const translationRef = doc(
        collection(programRef, "translations"),
        translationId
      );

      await runTransaction(db(), async transaction => {
        const translationDoc = await transaction.get(translationRef);

        if (!translationDoc.exists()) {
          throw new Error("Translation does not exist");
        }

        const currentData = translationDoc.data();
        const updatedDirectory = {
          ...currentData.directory,
          ...directory
        };

        transaction.update(translationRef, {
          directory: updatedDirectory,
          updated: serverTimestamp(),
          updatedBy: getters.user?.id || "system"
        });
      });

      // Update local state
      if (
        getters.currentTranslation &&
        getters.currentTranslation.id === translationId
      ) {
        const updatedTranslation = {
          ...getters.currentTranslation,
          directory: {
            ...getters.currentTranslation.directory,
            ...directory
          },
          updated: new Date()
        };
        commit("updateTranslation", updatedTranslation);
      }

      dispatch("setSnackbar", "Translation directory updated successfully");
      return true;
    } catch (error) {
      console.error("Error updating translation directory:", error);
      commit("setTranslationError", error.message);
      throw error;
    } finally {
      commit("setProcessingTranslation", false);
    }
  },

  /**
   * Get translations by locale
   */
  async getTranslationsByLocale({ getters }, locale) {
    if (!locale) {
      throw new Error("Locale is required");
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const translationsRef = collection(programRef, "translations");
      const localeQuery = query(
        translationsRef,
        where("locale", "==", locale),
        orderBy("titleUppercase")
      );
      const querySnapshot = await getDocs(localeQuery);

      const translations = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        translations.push({
          id: docSnapshot.id,
          title: data.title || "",
          titleUppercase: data.titleUppercase || "",
          locale: data.locale || "",
          directory: data.directory || {},
          status: data.status || "Draft",
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date(),
          createdBy: data.createdBy || "",
          updatedBy: data.updatedBy || ""
        });
      });

      return translations;
    } catch (error) {
      console.error("Error getting translations by locale:", error);
      throw error;
    }
  },

  // State management actions
  setTranslationChanged({ commit }, payload) {
    commit("setTranslationChanged", Boolean(payload));
  },

  setSavingTranslation({ commit }, payload) {
    commit("setSavingTranslation", Boolean(payload));
  },

  patchTranslation({ commit }, payload) {
    commit("patchTranslation", payload);
  },

  resetTranslationUpdates({ commit }) {
    commit("resetTranslationUpdates", {});
  },

  clearTranslationError({ commit }) {
    commit("setTranslationError", null);
  },

  clearCurrentTranslation({ commit }) {
    commit("setCurrentTranslation", null);
  },

  clearTranslations({ commit }) {
    commit("setTranslations", []);
  },

  clearTranslationData({ commit }) {
    commit("setTranslationData", {});
  },

  /**
   * Set locale and update i18n
   */
  setLocale({ dispatch }, locale) {
    try {
      console.log("🌐 Setting locale to:", locale);
      const i18n = require("@/plugins/i18n").default;
      i18n.setLocaleWithFallback(locale);

      // Update the store's initial locale
      this.commit("setInitialLocale", locale);

      console.log("✅ Locale updated successfully");
    } catch (error) {
      console.error("❌ Error setting locale:", error);
    }
  }
};

const mutations = {
  setTranslations(state, payload) {
    state.translations = payload;
  },

  setTranslationData(state, payload) {
    state.translationData = payload;
  },

  setCurrentTranslation(state, payload) {
    state.currentTranslation = payload;
  },

  setLoadingTranslations(state, payload) {
    state.loadingTranslations = Boolean(payload);
  },

  setLoadingTranslationData(state, payload) {
    state.loadingTranslationData = Boolean(payload);
  },

  setLoadingTranslation(state, payload) {
    state.loadingTranslation = Boolean(payload);
  },

  setSavingTranslation(state, payload) {
    state.savingTranslation = Boolean(payload);
  },

  setDeletingTranslation(state, payload) {
    state.deletingTranslation = Boolean(payload);
  },

  setProcessingTranslation(state, payload) {
    state.processingTranslation = Boolean(payload);
  },

  setTranslationError(state, payload) {
    state.error = payload;
  },

  resetTranslationUpdates(state, payload) {
    state.translationUpdates = { ...payload };
  },

  patchTranslation(state, payload) {
    state.translationUpdates = { ...payload };
  },

  setTranslationChanged(state, payload) {
    state.translationChanged = Boolean(payload);
  },

  updateTranslation(state, payload) {
    state.currentTranslation = payload;

    // Update in translations array if it exists
    if (state.translations.length > 0) {
      const index = state.translations.findIndex(
        translation => translation.id === payload.id
      );
      if (index !== -1) {
        state.translations[index] = {
          ...state.translations[index],
          ...payload
        };
      }
    }
  },

  createTranslation(state, payload) {
    state.translations = [payload, ...state.translations];
    state.currentTranslation = payload;
  },

  deleteTranslation(state, translationId) {
    state.translations = state.translations.filter(
      translation => translation.id !== translationId
    );

    // Clear current translation if it matches
    if (
      state.currentTranslation &&
      state.currentTranslation.id === translationId
    ) {
      state.currentTranslation = null;
    }
  }
};

const getters = {
  /**
   * Get all translations
   */
  translations(state) {
    return state.translations;
  },

  /**
   * Get processed translation data for i18n
   */
  translationData(state) {
    return state.translationData;
  },

  /**
   * Get current translation
   */
  currentTranslation(state) {
    return state.currentTranslation;
  },

  /**
   * Get translation updates
   */
  translationUpdates(state) {
    return state.translationUpdates;
  },

  /**
   * Check if translation has changed
   */
  translationChanged(state) {
    return state.translationChanged;
  },

  /**
   * Get loading states
   */
  loadingTranslations(state) {
    return state.loadingTranslations;
  },

  loadingTranslationData(state) {
    return state.loadingTranslationData;
  },

  loadingTranslation(state) {
    return state.loadingTranslation;
  },

  savingTranslation(state) {
    return state.savingTranslation;
  },

  deletingTranslation(state) {
    return state.deletingTranslation;
  },

  processingTranslation(state) {
    return state.processingTranslation;
  },

  /**
   * Get error state
   */
  translationError(state) {
    return state.error;
  },

  /**
   * Check if any translation operations are loading
   */
  translationIsLoading(state) {
    return (
      state.loadingTranslations ||
      state.loadingTranslationData ||
      state.loadingTranslation ||
      state.savingTranslation ||
      state.deletingTranslation ||
      state.processingTranslation
    );
  },

  /**
   * Get translation by ID from state
   */
  getTranslationById: state => translationId => {
    return (
      state.translations.find(
        translation => translation.id === translationId
      ) || null
    );
  },

  /**
   * Get translations by locale
   */
  getTranslationsByLocale: state => locale => {
    return state.translations.filter(
      translation => translation.locale === locale
    );
  },

  /**
   * Get translations by status
   */
  getTranslationsByStatus: state => status => {
    return state.translations.filter(
      translation => translation.status === status
    );
  },

  /**
   * Get published translations
   */
  publishedTranslations(state) {
    return state.translations.filter(
      translation => translation.status === "Published"
    );
  },

  /**
   * Get draft translations
   */
  draftTranslations(state) {
    return state.translations.filter(
      translation => translation.status === "Draft"
    );
  },

  /**
   * Get translations count
   */
  translationsCount(state) {
    return state.translations.length;
  },

  /**
   * Check if translations exist
   */
  hasTranslations(state) {
    return state.translations.length > 0;
  },

  /**
   * Get available locales
   */
  availableLocales(state) {
    const locales = [
      ...new Set(state.translations.map(translation => translation.locale))
    ];
    return locales.filter(locale => locale); // Remove empty locales
  },

  /**
   * Get locale statistics
   */
  localeStatistics(state) {
    const stats = {};
    state.translations.forEach(translation => {
      const locale = translation.locale || "unknown";
      if (!stats[locale]) {
        stats[locale] = {
          total: 0,
          published: 0,
          draft: 0
        };
      }
      stats[locale].total++;
      if (translation.status === "Published") {
        stats[locale].published++;
      } else if (translation.status === "Draft") {
        stats[locale].draft++;
      }
    });
    return stats;
  },

  /**
   * Get translations as options for dropdowns
   */
  translationsOptions(state) {
    return state.translations.map(translation => ({
      text: `${translation.title} (${translation.locale})`,
      value: translation.id
    }));
  },

  /**
   * Get translations options by locale
   */
  getTranslationsOptionsByLocale: state => locale => {
    return state.translations
      .filter(translation => translation.locale === locale)
      .map(translation => ({
        text: translation.title,
        value: translation.id
      }));
  },

  /**
   * Get recent translations
   */
  recentTranslations(state) {
    return state.translations
      .sort((a, b) => new Date(b.updated) - new Date(a.updated))
      .slice(0, 10);
  },

  /**
   * Search translations by title
   */
  searchTranslations: state => searchTerm => {
    if (!searchTerm) return state.translations;

    const term = searchTerm.toLowerCase();
    return state.translations.filter(
      translation =>
        translation.title.toLowerCase().includes(term) ||
        translation.locale.toLowerCase().includes(term)
    );
  },

  /**
   * Get translations grouped by locale
   */
  translationsGroupedByLocale(state) {
    return state.translations.reduce((groups, translation) => {
      const locale = translation.locale || "unknown";
      if (!groups[locale]) {
        groups[locale] = [];
      }
      groups[locale].push(translation);
      return groups;
    }, {});
  },

  /**
   * Get translation statistics
   */
  translationStatistics(state) {
    const total = state.translations.length;
    const published = state.translations.filter(t => t.status === "Published")
      .length;
    const draft = state.translations.filter(t => t.status === "Draft").length;
    const locales = new Set(state.translations.map(t => t.locale)).size;

    return {
      total,
      published,
      draft,
      locales
    };
  },

  /**
   * Check if locale has translations
   */
  hasTranslationsForLocale: state => locale => {
    return state.translations.some(
      translation => translation.locale === locale
    );
  },

  /**
   * Get translation directory keys count
   */
  getTranslationKeysCount: state => translationId => {
    const translation = state.translations.find(t => t.id === translationId);
    return translation ? Object.keys(translation.directory || {}).length : 0;
  },

  /**
   * Check if translation is valid for publishing
   */
  isTranslationValidForPublishing: state => translationId => {
    const translation = state.translations.find(t => t.id === translationId);
    return (
      translation &&
      translation.title &&
      translation.locale &&
      translation.directory &&
      Object.keys(translation.directory).length > 0
    );
  },

  /**
   * Get translation completion percentage
   */
  getTranslationCompletionPercentage: state => translationId => {
    const translation = state.translations.find(t => t.id === translationId);
    if (!translation || !translation.directory) return 0;

    const directory = translation.directory;
    const totalKeys = Object.keys(directory).length;
    if (totalKeys === 0) return 0;

    const completedKeys = Object.values(directory).filter(
      item =>
        item && typeof item === "object" && item.value && item.value.trim()
    ).length;

    return Math.round((completedKeys / totalKeys) * 100);
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
